import {
  GraphTitle,
  GraphTitleOperation,
  HalfPageModule,
} from '../../../styles/global.styles';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
  Tooltip,
} from 'recharts';
import { ReactComponent as CustomDot } from '../../../assets/images/customChartDot.svg';
import { FormattedMessage, useIntl } from 'react-intl';
import { useState, useEffect } from 'react';
import { ScatterDataModel } from '../../../types/indicators.interfaces';
import { numberFormat } from '../../../helpers/utils';
import { formatCurrencyExtended, formatTickPrice } from '../../../helpers/currencyFormatter';
import {
  AssetAreaLabel,
  AssetValueLabel,
  YourAssetLabel,
} from './comparativeGraph.styles';
import { LoadingSpinner } from '../../../styles/global.styles';
import { useApiStore } from '../../../zustand/api.store';
import { getCorrectPrice } from '../../../helpers/priceHelpers';

interface Comparables {
  x: number;
  y: number;
  z: number;
  area: number;
  distance: number;
  id: string;
  original_id: number;
  _id: string;
}

const CustomLabel = ({
  viewBox,
  labels,
}: {
  viewBox: any;
  labels: string[];
}) => {
  const { x, y } = viewBox;
  const labelOffset = 15;
  return (
    <g fill="black" style={{ backgroundColor: 'black' }}>
      <YourAssetLabel x={x} y={y - labelOffset - 15 * 3}>
        {labels[0]}
      </YourAssetLabel>
      <AssetValueLabel x={x} y={y - labelOffset - 15}>
        {labels[1]}
      </AssetValueLabel>
      <AssetAreaLabel x={x} y={y - labelOffset + 8}>
        {labels[2]}
      </AssetAreaLabel>
    </g>
  );
};

export const AssetComparativeGraph = (props: ScatterDataModel & { country?: string; useCorrectedPrice?: boolean }) => {
  const intl = useIntl();
  const [AVMComps, setAVMComps] = useState<Comparables[]>();
  const [minArea, setMinArea] = useState<number>();
  const [maxArea, setMaxArea] = useState<number>();
  const [similarComps, setSimilarComps] = useState<Comparables[]>();
  const [isLoading, setIsLoading] = useState(true);
  const paramsURL = new URLSearchParams(window.location.search);
  const listingType = paramsURL.get('listing_type');
  const country = props.country || 'es';
  const useCorrectedPrice = props.useCorrectedPrice || false;

  // Get translated suffixes
  const sqmSuffix = intl.formatMessage({ id: 'sqmPriceSuffix' });
  const sqmSuffix2 = intl.formatMessage({ id: 'sqmSuffix' });

  // Function to format price based on listing type
  const formatChartPrice = (value: number) => {
    if (listingType === '7' || listingType === '11') {
      return formatCurrencyExtended(value, country, false, sqmSuffix);
    } else {
      return formatCurrencyExtended(value, country, true, sqmSuffix);
    }
  };

  // Function to format price for tooltip
  const formatTooltipPrice = (value: number) => {
    if (listingType === '7' || listingType === '11') {
      return formatCurrencyExtended(value, country, false, sqmSuffix);
    } else {
      return formatCurrencyExtended(value, country, true, sqmSuffix);
    }
  };

  // Function to format price for Y-axis ticks (simplified to avoid rendering issues)
  const formatYAxisTick = (value: number) => {
    return formatTickPrice(value, country, listingType || undefined, sqmSuffix);
  };

  const getAVMMinMaxX = (avmComps: Comparables[] | null) => {
    if (!avmComps) return [0, 0];
    const avmAreas = avmComps.map((comp) => comp.x);
    const minAvm = Math.min(...avmAreas);
    const maxAvm = Math.max(...avmAreas);

    return [minAvm, maxAvm];
  };

  // Function to detect outliers using IQR (Interquartile Range) method
  // Returns an array of indices that are NOT outliers
  const detectOutliers = (values: number[]): number[] => {
    if (values.length < 4) return values.map((_, i) => i); // Need at least 4 values for IQR
    
    // Sort values to calculate quartiles
    const sortedValues = [...values].sort((a, b) => a - b);
    const q1Index = Math.floor(sortedValues.length * 0.25);
    const q3Index = Math.floor(sortedValues.length * 0.75);
    const q1 = sortedValues[q1Index];
    const q3 = sortedValues[q3Index];
    const iqr = q3 - q1;
    
    // Calculate bounds (using 1.5 * IQR as standard)
    const lowerBound = q1 - 1.5 * iqr;
    const upperBound = q3 + 1.5 * iqr;
    
    // Return indices of values that are NOT outliers
    return values
      .map((value, index) => ({ value, index }))
      .filter(({ value }) => value >= lowerBound && value <= upperBound)
      .map(({ index }) => index);
  };

  useEffect(() => {
    setIsLoading(true);
    if (props.similarComparables) {
      // First, filter valid comparables and extract prices for outlier detection
      const validComparables = props.similarComparables
        .map((comp) => {
          const isMainPrice = listingType === '7' || listingType === '11';
          const price = getCorrectPrice(comp, isMainPrice, useCorrectedPrice);
          
          if (
            comp.area &&
            comp.area !== 0 &&
            price &&
            price !== 0
          ) {
            return { comp, price };
          }
          return null;
        })
        .filter((item): item is { comp: any; price: number } => item !== null);

      // Detect outliers based on the prices that will be used in the graph
      let filteredComparables = validComparables;
      if (validComparables.length >= 4) {
        const prices = validComparables.map(item => item.price);
        const validIndices = detectOutliers(prices);
        filteredComparables = validComparables.filter((_, idx) => validIndices.includes(idx));
      }

      // Format the filtered comparables
      const formattedSimilar = filteredComparables.map(({ comp, price }) => ({
        x: comp.area,
        y: Number(price.toFixed(0)),
        z: 5,
        area: comp.area,
        distance: comp.distance,
        id: comp.id?.toString() || '',
        original_id: comp.original_id,
        _id: comp._id
      }));
      
      setSimilarComps(formattedSimilar);
    }
    if (props.comparables) {
      const formattedSimilar = props.comparables
        .map((comp) => {
          const isMainPrice = listingType === '7' || listingType === '11';
          const price = getCorrectPrice(comp, isMainPrice, useCorrectedPrice);
          
          if (
            comp.area &&
            comp.area !== 0 &&
            price &&
            price !== 0
          ) {
            return {
              x: comp.area ?? 0,
              y: Number(price?.toFixed(0)) ?? 0,
              z: 5,
              area: comp.area ?? 0,
              distance: comp.distance,
              id: comp.id?.toString() || '',
              original_id: comp.original_id,
              _id: comp._id
            };
          }
          return undefined;
        })
        .filter((comp): comp is Comparables => comp !== undefined);
      setAVMComps(formattedSimilar);
    }
    setIsLoading(false);
  }, [props, listingType]);

  useEffect(() => {
    if (AVMComps && similarComps && props.asset?.[0]) {
      const avmAreas = AVMComps?.map((comp) => comp.x);
      const minAvm = Math.min(...(avmAreas || []));
      const maxAvm = Math.max(...(avmAreas || []));

      const similarAreas = similarComps?.map((comp) => comp.x);
      const minSimilar = Math.min(...(similarAreas || []));
      const maxSimilar = Math.max(...(similarAreas || []));

      const assetArea = props.asset[0].x;

      const minArea = Math.min(...[minAvm, minSimilar, assetArea]);
      const maxArea = Math.max(...[maxAvm, maxSimilar, assetArea]);

      // Add 10% padding on each side
      const range = maxArea - minArea;
      const padding = range * 0.1;

      setMinArea(Math.max(0, minArea - padding)); // Ensure we don't go below 0
      setMaxArea(maxArea + padding);
    }
  }, [AVMComps, similarComps, props.asset]);

  const assetXValue = props.asset?.[0]?.x || 0;
  const assetYValue = props.asset?.[0]?.y || 0;
  const [minAvmX, maxAvmX] = getAVMMinMaxX(AVMComps || null);

  // Función manejadora de clics para abrir URL del comparable
  const handlePointClick = (data: any) => {
    // No abrir URL si es el activo principal del usuario
    if (data.x === assetXValue && data.y === assetYValue) {
      return;
    }
    
    // Abrir URL en nueva pestaña si existe
    if (data._id) {
      window.open(`${process.env.REACT_APP_PULSE_URL}/asset/detail/${data._id}?country=${country}`, '_blank');
    }
    else {
      window.open(`${process.env.REACT_APP_PULSE_URL}/asset/detail/${data._id}?country=${country}`, '_blank');
    }
  };

  // Función personalizada para el tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      // No mostrar tooltip si es el activo principal del usuario
      if (data.x === assetXValue && data.y === assetYValue) {
        return null;
      }
      console.log('assetYValue', assetYValue);
      
      return (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '4px 6px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: '0 0 2px 0', fontWeight: 'bold', fontSize: '12px' }}>
            {intl.formatMessage({ id: 'area' })}: {numberFormat.format(data.area)} {sqmSuffix2}
          </p>
          <p style={{ margin: '0 0 2px 0', fontSize: '12px' }}>
            {intl.formatMessage({ id: props.operation === 1 ? 'valuation.salePriceTitle' : 'valuation.rentPriceTitle' })}: {formatTooltipPrice(data.y)}
          </p>
          <p style={{ margin: '0', fontSize: '12px' }}>
            {intl.formatMessage({ id: 'comparables.distance' })}: {Math.round(data.distance)} m
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {isLoading ? (
        <HalfPageModule>
          <LoadingSpinner />
        </HalfPageModule>
      ) : (
        AVMComps &&
        similarComps && (
          <HalfPageModule>
            <GraphTitle>
              <FormattedMessage
                id="comparativeGraph.title"
                values={{
                  operation: (
                    <GraphTitleOperation color={'#1778FB'}>
                      {props.operation == 1 ? (
                        <FormattedMessage id="marketGraph.subtitleOperationSale" />
                      ) : (
                        <FormattedMessage id="marketGraph.subtitleOperationRent" />
                      )}
                    </GraphTitleOperation>
                  ),
                }}
              />
            </GraphTitle>
            {(AVMComps || similarComps) &&
              minArea !== undefined &&
              maxArea !== undefined &&
              minArea !== null &&
              maxArea !== null && (
                <ResponsiveContainer width="100%" height={'100%'}>
                  <ScatterChart
                    margin={{ top: 70, right: 20, bottom: 20, left: 45 }}
                  >
                    <CartesianGrid vertical={false} strokeDasharray={'3 3'} />
                    <Tooltip content={<CustomTooltip />} />
                    {AVMComps && (
                      <ReferenceArea
                        x1={minAvmX}
                        x2={maxAvmX}
                        strokeOpacity={0.3}
                        fill="#9dd8ff80"
                        ifOverflow="extendDomain"
                      />
                    )}
                    <XAxis
                      type="number"
                      dataKey="x"
                      name="X-Value"
                      tickFormatter={(value) => `${value.toFixed(0)} ${sqmSuffix2}`}
                      tickMargin={12}
                      domain={[minArea, maxArea]}
                      axisLine={false}
                      tickLine={true}
                      ticks={[
                        minArea,
                        ...Array.from(
                          { length: 3 },
                          (_, i) =>
                            minArea + ((maxArea - minArea) * (i + 1)) / 4
                        ),
                        maxArea,
                      ]}
                    />
                    <YAxis
                      type="number"
                      dataKey="y"
                      name="Y-Value"
                      tickFormatter={formatYAxisTick}
                      fontSize={'14'}
                      tickMargin={15}
                      tickCount={5}
                      domain={['auto', 'auto']}
                      axisLine={false}
                      tickLine={false}
                      width={75}
                    />
                    <ZAxis
                      type="number"
                      dataKey="z"
                      range={[70, 230]}
                      name="Z-Value"
                    />
                    <Legend
                      layout="horizontal"
                      verticalAlign="bottom"
                      align="center"
                      margin={{ top: 30, right: 20, bottom: 0, left: 0 }}
                      wrapperStyle={{
                        padding: '20px',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    />
                    <ReferenceLine
                      x={assetXValue}
                      stroke="#0C1B7A"
                      strokeDasharray="3 3"
                      label={
                        <CustomLabel
                          viewBox={{ x: assetXValue, y: 20 }} // Adjust y position
                          labels={[
                            intl.formatMessage({ id: `marketGraph.yourAsset` }),
                            formatChartPrice(Number(assetYValue.toFixed(0))), 
                            `${numberFormat.format(assetXValue)} ${sqmSuffix2}`,
                          ]}
                        />
                      }
                    />
                    <Scatter
                      name={intl.formatMessage({
                        id: `comparativeGraph.legendOther`,
                      })}
                      data={similarComps}
                      shape="circle"
                      fillOpacity={1}
                      fill={'#B6BBC6'}
                      onClick={handlePointClick}
                    />
                    <Scatter
                      name={
                        props.reportType === 2
                          ? intl.formatMessage({
                              id: 'comparativeGraph.legendSelected',
                            })
                          : intl.formatMessage({
                              id: 'comparativeGraph.legendAVM',
                            })
                      }
                      data={AVMComps}
                      shape="circle"
                      fillOpacity={1}
                      fill={'#1778FB'}
                      onClick={handlePointClick}
                    />
                    <Scatter
                      name={intl.formatMessage({ id: `marketGraph.yourAsset` })}
                      data={props.asset}
                      shape={<CustomDot />}
                    />
                  </ScatterChart>
                </ResponsiveContainer>
              )}
          </HalfPageModule>
        )
      )}
    </>
  );
};
